import { formatNumber } from "@/v2-common/utils/utils.formatting"
import cn from "@/v2-ui/utils/utils.cn"
import Label from "@/v2-ui/label"
import Link from "@/v2-backoffice-client/components/link"
import RelativeDate from "@/v2-backoffice-client/components/RelativeDate"
import UsageTrendBadge from "@/v2-backoffice-client/usage/UsageTrendBadge"
import { buildCustomerViewLink } from "@/v2-backoffice-client/customer/customer.links"
import { formatTrendDetail } from "@/v2-backoffice-client/usage/usage.constants"
import { mcpTrendPct } from "@/v2-backoffice-client/usage/mcp/usage.mcp.utils"
import { useUsageLicenseDeniedFeatureQuery } from "@/v2-backoffice-client/usage/denied/usage.deniedFeature.query"

export type FeatureAccessDemandProps = {
  featureCode: string
  /** True while the saved row sits behind the package lock. */
  isLocked: boolean
  className?: string
}

/**
 * The demand a feature meets while locked — how many people at how many
 * customers tried to use it and were refused in the last 28 days, and who
 * knocked hardest — read on the feature's access panel, next to who has it.
 * It is the number to weigh before deciding whether a locked feature stays
 * locked, moves to another package, or is sold to the customers asking.
 * Quiet for a free feature nobody has ever been refused.
 */
function FeatureAccessDemand(props: FeatureAccessDemandProps) {
  const { featureCode, isLocked, className } = props

  const { data } = useUsageLicenseDeniedFeatureQuery({
    variables: { featureCode },
  })
  const demand = data?.getUsageLicenseDeniedByFeature
  if (!demand) return null
  if (demand.Attempts28d === 0 && demand.AttemptsPrev28d === 0 && !isLocked) {
    return null
  }

  return (
    <div className={cn("space-y-1", className)}>
      <Label>Asked for while locked</Label>
      {demand.Attempts28d === 0 ? (
        <p className="text-sm text-muted-foreground">
          Nobody has been refused this feature in the last 28 days
          {demand.AttemptsPrev28d > 0 &&
            ` (${formatNumber(demand.AttemptsPrev28d)} refused attempts the 28 days before)`}
          .
        </p>
      ) : (
        <>
          <p className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-sm">
            <span>
              {formatNumber(demand.Attempts28d)} refused attempt
              {demand.Attempts28d === 1 ? "" : "s"} by {demand.Users28d}{" "}
              {demand.Users28d === 1 ? "person" : "people"} at{" "}
              {demand.Customers28d}{" "}
              {demand.Customers28d === 1 ? "customer" : "customers"} in the last
              28 days
              {demand.LastAt && (
                <>
                  , last <RelativeDate date={demand.LastAt} />
                </>
              )}
              .
            </span>
            <UsageTrendBadge
              trendPct={mcpTrendPct(demand.Attempts28d, demand.AttemptsPrev28d)}
              detail={formatTrendDetail(
                demand.Attempts28d,
                demand.AttemptsPrev28d,
              )}
            />
          </p>
          {demand.Customers.length > 0 && (
            <p className="text-xs text-muted-foreground">
              Knocking hardest:{" "}
              {demand.Customers.map((customer, index) => (
                <span key={customer.ExternalCustomerId}>
                  {index > 0 && " · "}
                  <Link
                    variant="muted"
                    size="xs"
                    iconSrc={null}
                    className="underline underline-offset-2"
                    to={buildCustomerViewLink(
                      customer.ExternalCustomerId,
                      "sign-ins",
                    )}
                  >
                    {customer.Name}
                  </Link>{" "}
                  ({formatNumber(customer.Attempts)}
                  {customer.Users > 1 && `, ${customer.Users} people`})
                </span>
              ))}
              . The packages that carry this feature are the upsell.
            </p>
          )}
        </>
      )}
    </div>
  )
}

export default FeatureAccessDemand
